import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { API } from "../App";
import { Sidebar } from "../components/Sidebar";
import { LeaveRequestForm } from "../components/LeaveRequestForm";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { ScrollArea } from "../components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { Sheet, SheetContent } from "../components/ui/sheet";
import { toast } from "sonner";
import { 
  Calendar as CalendarIcon,
  CheckCircle2, 
  XCircle, 
  Hourglass,
  Users,
  Clock,
  Eye,
  MessageSquare,
  AlertTriangle,
  Menu,
  ChevronLeft,
  ChevronRight,
  TreePalm
} from "lucide-react";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isWeekend, parseISO, isWithinInterval, addMonths, isToday, addDays } from "date-fns";
import { fr } from "date-fns/locale";

// Timeline bar for a team member (monthly view)
const TeamTimeline = ({ teamRequests, currentMonth }) => {
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
  
  const userLeaves = useMemo(() => {
    const map = {};
    teamRequests.filter(r => r.status === 'approved').forEach(r => {
      if (!map[r.user_id]) map[r.user_id] = { name: r.user_name, leaves: [] };
      map[r.user_id].leaves.push(r);
    });
    return Object.values(map);
  }, [teamRequests]);

  const isInLeave = (day, leaves) => {
    return leaves.some(l => {
      const s = parseISO(l.start_date);
      const e = parseISO(l.end_date);
      return isWithinInterval(day, { start: s, end: e });
    });
  };

  if (userLeaves.length === 0) {
    return (
      <div className="text-center py-8 text-gray-400">
        <TreePalm className="h-8 w-8 mx-auto mb-2" />
        <p className="text-sm">Aucune absence ce mois-ci</p>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <div className="flex items-center">
        <div className="w-24 lg:w-32 flex-shrink-0" />
        <div className="flex-1 flex">
          {days.map((day, i) => (
            <div key={i} className={`flex-1 text-center text-[9px] lg:text-[11px] py-0.5 ${isWeekend(day) ? 'text-gray-300' : isToday(day) ? 'text-[#00A3E0] font-bold' : 'text-gray-400'}`}>
              {format(day, 'd')}
            </div>
          ))}
        </div>
      </div>
      {userLeaves.map((user, idx) => (
        <div key={idx} className="flex items-center">
          <div className="w-24 lg:w-32 flex-shrink-0 flex items-center gap-1.5 pr-2">
            <div className="w-6 h-6 rounded-full bg-[#00A3E0] text-white flex items-center justify-center text-[10px] font-bold flex-shrink-0">
              {user.name.split(' ').map(n => n[0]).join('').substring(0, 2)}
            </div>
            <span className="text-xs text-gray-700 truncate">{user.name.split(' ')[0]}</span>
          </div>
          <div className="flex-1 flex h-7 gap-px">
            {days.map((day, i) => {
              const inLeave = isInLeave(day, user.leaves);
              return (
                <div key={i} className={`flex-1 rounded-sm transition-all ${isWeekend(day) ? 'bg-gray-100' : inLeave ? 'bg-[#00A3E0]' : 'bg-gray-50'}`} />
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

// Visual conflict timeline for the review dialog
const ConflictTimeline = ({ request, teamRequests }) => {
  const reqStart = parseISO(request.start_date);
  const reqEnd = parseISO(request.end_date);
  const viewStart = addDays(reqStart, -1);
  const viewEnd = addDays(reqEnd, 1);
  const days = eachDayOfInterval({ start: viewStart, end: viewEnd });

  const overlapping = useMemo(() => {
    const map = {};
    teamRequests.filter(r => r.status === 'approved' && r.user_id !== request.user_id).forEach(r => {
      const s = parseISO(r.start_date);
      const e = parseISO(r.end_date);
      if (s <= viewEnd && e >= viewStart) {
        if (!map[r.user_id]) map[r.user_id] = { name: r.user_name, leaves: [] };
        map[r.user_id].leaves.push(r);
      }
    });
    return Object.values(map);
  }, [teamRequests, request, viewStart, viewEnd]);

  const isInLeave = (day, leaves) => {
    return leaves.some(l => isWithinInterval(day, { start: parseISO(l.start_date), end: parseISO(l.end_date) }));
  };
  const isInRequest = (day) => isWithinInterval(day, { start: reqStart, end: reqEnd });

  return (
    <div className="space-y-1" data-testid="conflict-timeline">
      {/* Day headers */}
      <div className="flex items-center">
        <div className="w-20 lg:w-24 flex-shrink-0" />
        <div className="flex-1 flex">
          {days.map((day, i) => (
            <div key={i} className={`flex-1 text-center text-[8px] lg:text-[10px] py-0.5 leading-tight ${isWeekend(day) ? 'text-gray-300' : isInRequest(day) ? 'text-amber-600 font-bold' : 'text-gray-400'}`}>
              <div>{format(day, 'd')}</div>
              <div className="hidden lg:block">{format(day, 'MMM', { locale: fr })}</div>
            </div>
          ))}
        </div>
      </div>
      {/* Requesting user's row */}
      <div className="flex items-center">
        <div className="w-20 lg:w-24 flex-shrink-0 flex items-center gap-1 pr-1">
          <div className="w-5 h-5 rounded-full bg-amber-500 text-white flex items-center justify-center text-[8px] font-bold flex-shrink-0">
            {request.user_name.split(' ').map(n => n[0]).join('').substring(0, 2)}
          </div>
          <span className="text-[10px] font-semibold text-amber-700 truncate">{request.user_name.split(' ')[0]}</span>
        </div>
        <div className="flex-1 flex h-6 gap-px">
          {days.map((day, i) => (
            <div key={i} className={`flex-1 rounded-sm ${isWeekend(day) ? 'bg-gray-100' : isInRequest(day) ? 'bg-amber-400' : 'bg-gray-50'}`} />
          ))}
        </div>
      </div>
      {/* Other team members */}
      {overlapping.map((user, idx) => (
        <div key={idx} className="flex items-center">
          <div className="w-20 lg:w-24 flex-shrink-0 flex items-center gap-1 pr-1">
            <div className="w-5 h-5 rounded-full bg-[#00A3E0] text-white flex items-center justify-center text-[8px] font-bold flex-shrink-0">
              {user.name.split(' ').map(n => n[0]).join('').substring(0, 2)}
            </div>
            <span className="text-[10px] text-gray-600 truncate">{user.name.split(' ')[0]}</span>
          </div>
          <div className="flex-1 flex h-6 gap-px">
            {days.map((day, i) => {
              const inLeave = isInLeave(day, user.leaves);
              return (
                <div key={i} className={`flex-1 rounded-sm ${isWeekend(day) ? 'bg-gray-100' : inLeave ? 'bg-[#00A3E0]' : 'bg-gray-50'}`} />
              );
            })}
          </div>
        </div>
      ))}
      {overlapping.length === 0 && (
        <div className="flex items-center justify-center py-2 text-[11px] text-emerald-600">
          <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
          Aucun collègue absent sur cette période
        </div>
      )}
      {/* Legend */}
      <div className="flex items-center gap-4 pt-2 border-t border-gray-100 mt-1">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-sm bg-amber-400" />
          <span className="text-[10px] text-gray-500">Demande en cours</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-sm bg-[#00A3E0]" />
          <span className="text-[10px] text-gray-500">Absence validée</span>
        </div>
      </div>
    </div>
  );
};

// CP balance card for a team member
const BalanceCard = ({ member }) => {
  const pct = member.cp_total > 0 ? ((member.cp_remaining / member.cp_total) * 100) : 0;
  const isLow = member.cp_remaining <= 5;
  const isNegative = member.cp_remaining < 0;
  return (
    <div className="flex items-center gap-3 p-3 rounded-lg bg-white border border-gray-100" data-testid={`balance-${member.user_id}`}>
      <div className="w-9 h-9 rounded-full bg-[#1a2634] text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
        {member.user_name.split(' ').map(n => n[0]).join('').substring(0, 2)}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate">{member.user_name}</p>
        <div className="flex items-center justify-end mt-0.5 mb-1">
          <span className={`text-xs font-bold ${isNegative ? 'text-red-500' : isLow ? 'text-amber-500' : 'text-[#00A3E0]'}`}>
            Restant : {member.cp_remaining}j / {member.cp_total}j
          </span>
        </div>
        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
          <div className={`h-full rounded-full transition-all ${isNegative ? 'bg-red-400' : isLow ? 'bg-amber-400' : 'bg-[#00A3E0]'}`} style={{ width: `${Math.max(0, Math.min(100, pct))}%` }} />
        </div>
      </div>
      {member.cp_pending > 0 && (
        <Badge variant="outline" className="bg-amber-50 text-amber-600 border-amber-200 text-[10px] flex-shrink-0">
          {member.cp_pending}j att.
        </Badge>
      )}
    </div>
  );
};

export default function ManagerDashboard() {
  const [pendingRequests, setPendingRequests] = useState([]);
  const [teamRequests, setTeamRequests] = useState([]);
  const [teamBalances, setTeamBalances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [reviewComment, setReviewComment] = useState("");
  const [reviewLoading, setReviewLoading] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const { getAuthHeader } = useAuth();

  const fetchData = async () => {
    try {
      const headers = getAuthHeader();
      const [pendingRes, teamRes, balancesRes] = await Promise.all([
        axios.get(`${API}/leave-requests/pending`, headers),
        axios.get(`${API}/leave-requests/team`, headers),
        axios.get(`${API}/team/balances`, headers)
      ]);
      setPendingRequests(pendingRes.data);
      setTeamRequests(teamRes.data);
      setTeamBalances(balancesRes.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleReview = async (status) => {
    if (!selectedRequest) return;
    setReviewLoading(true);
    try {
      await axios.put(
        `${API}/leave-requests/${selectedRequest.id}/review`,
        { status, comment: reviewComment },
        getAuthHeader()
      );
      toast.success(status === 'approved' ? 'Demande approuvée' : 'Demande refusée');
      setSelectedRequest(null);
      setReviewComment("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la validation");
    } finally {
      setReviewLoading(false);
    }
  };

  const monthRequests = useMemo(() => {
    return teamRequests.filter(r => {
      const start = parseISO(r.start_date);
      const end = parseISO(r.end_date);
      const ms = startOfMonth(currentMonth);
      const me = endOfMonth(currentMonth);
      return isWithinInterval(start, { start: ms, end: me }) ||
             isWithinInterval(end, { start: ms, end: me }) ||
             (start <= ms && end >= me);
    });
  }, [teamRequests, currentMonth]);

  const getConflicts = (request) => {
    if (!request) return [];
    const rs = parseISO(request.start_date);
    const re = parseISO(request.end_date);
    return teamRequests.filter(l => {
      if (l.id === request.id || l.status !== 'approved' || l.user_id === request.user_id) return false;
      const ls = parseISO(l.start_date);
      const le = parseISO(l.end_date);
      return isWithinInterval(rs, { start: ls, end: le }) || isWithinInterval(re, { start: ls, end: le }) || isWithinInterval(ls, { start: rs, end: re });
    });
  };

  const getStatusBadge = (status) => {
    const s = { pending: { c: "bg-yellow-50 text-yellow-700 border-yellow-200", i: Hourglass, l: "En attente" }, approved: { c: "bg-green-50 text-green-700 border-green-200", i: CheckCircle2, l: "Approuvé" }, rejected: { c: "bg-red-50 text-red-700 border-red-200", i: XCircle, l: "Refusé" } };
    const st = s[status] || s.pending;
    return <Badge variant="outline" className={`${st.c} border`}><st.i className="h-3 w-3 mr-1" />{st.l}</Badge>;
  };

  if (loading) return (
    <div className="flex min-h-screen">
      <div className="hidden lg:block"><Sidebar /></div>
      <main className="flex-1 bg-gray-50 p-4 lg:p-8"><div className="animate-pulse space-y-6"><div className="h-8 bg-gray-200 rounded w-1/4"></div><div className="h-64 bg-gray-200 rounded-lg"></div></div></main>
    </div>
  );

  return (
    <div className="flex min-h-screen">
      <div className="hidden lg:block"><Sidebar /></div>
      <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        <SheetContent side="left" className="p-0 w-64"><Sidebar /></SheetContent>
      </Sheet>

      <main className="flex-1 bg-gray-50">
        {/* Header */}
        <header className="bg-white border-b border-gray-100 px-4 lg:px-8 py-4 lg:py-6 sticky top-0 z-40">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setMobileMenuOpen(true)}>
                <Menu className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-lg lg:text-2xl font-bold text-[#1a2634]" style={{ fontFamily: 'Chivo, sans-serif' }}>Mon équipe</h1>
                <p className="text-gray-500 text-sm hidden sm:block">Planning et gestion des congés</p>
              </div>
            </div>
            <div className="flex items-center gap-2 lg:gap-3">
              {pendingRequests.length > 0 && (
                <div className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-full">
                  <Hourglass className="h-3.5 w-3.5 text-amber-600" />
                  <span className="font-semibold text-amber-700 text-sm">{pendingRequests.length}</span>
                  <span className="text-amber-600 text-xs hidden lg:inline">en attente</span>
                </div>
              )}
              <LeaveRequestForm onSuccess={fetchData} />
            </div>
          </div>
        </header>

        <div className="p-4 lg:p-8">
          <Tabs defaultValue="team" className="space-y-4 lg:space-y-6">
            <TabsList className="bg-white border shadow-sm w-full lg:w-auto">
              <TabsTrigger value="team" className="data-[state=active]:bg-gray-100 text-xs lg:text-sm" data-testid="tab-team">
                <Users className="h-3.5 w-3.5 lg:h-4 lg:w-4 mr-1 lg:mr-2" />
                Équipe
                {pendingRequests.length > 0 && (
                  <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-bold">{pendingRequests.length}</span>
                )}
              </TabsTrigger>
              <TabsTrigger value="history" className="data-[state=active]:bg-gray-100 text-xs lg:text-sm" data-testid="tab-history">
                <Clock className="h-3.5 w-3.5 lg:h-4 lg:w-4 mr-1 lg:mr-2" />
                Historique
              </TabsTrigger>
            </TabsList>

            {/* ==================== TEAM TAB ==================== */}
            <TabsContent value="team" className="space-y-4 lg:space-y-6">
              {/* Team CP Balances */}
              <Card className="border-0 shadow-sm">
                <div className="px-4 lg:px-6 py-3 border-b border-gray-100 flex items-center gap-2">
                  <Users className="h-4 w-4 text-[#00A3E0]" />
                  <h3 className="text-sm lg:text-base font-semibold text-[#1a2634]">Solde CP de l'équipe</h3>
                </div>
                <CardContent className="p-3 lg:p-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    {teamBalances.map(member => (
                      <BalanceCard key={member.user_id} member={member} />
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Timeline */}
              <Card className="border-0 shadow-sm">
                <div className="px-4 lg:px-6 py-3 border-b border-gray-100 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CalendarIcon className="h-4 w-4 text-[#E63946]" />
                    <h3 className="text-sm lg:text-base font-semibold text-[#1a2634]">
                      {format(currentMonth, "MMMM yyyy", { locale: fr })}
                    </h3>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setCurrentMonth(addMonths(currentMonth, -1))}><ChevronLeft className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}><ChevronRight className="h-4 w-4" /></Button>
                  </div>
                </div>
                <CardContent className="p-3 lg:p-4 overflow-x-auto">
                  <TeamTimeline teamRequests={monthRequests} currentMonth={currentMonth} />
                </CardContent>
              </Card>

              {/* Pending Requests */}
              <Card className="border-0 shadow-sm">
                <div className="px-4 lg:px-6 py-3 border-b border-gray-100 flex items-center justify-between">
                  <h3 className="text-sm lg:text-base font-semibold flex items-center gap-2">
                    <Hourglass className="h-4 w-4 text-amber-500" />
                    Demandes à valider
                  </h3>
                  {pendingRequests.length > 0 && (
                    <Badge className="bg-amber-100 text-amber-700 border-0">{pendingRequests.length}</Badge>
                  )}
                </div>
                {pendingRequests.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                    <CheckCircle2 className="h-8 w-8 mb-2" />
                    <p className="text-sm">Aucune demande en attente</p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {pendingRequests.map((request) => {
                      const conflicts = getConflicts(request);
                      return (
                        <div key={request.id} className="p-4 lg:p-5 hover:bg-gray-50/50 transition-colors" data-testid={`pending-request-${request.id}`}>
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-[#00A3E0] text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">
                                {request.user_name.split(' ').map(n => n[0]).join('').substring(0, 2)}
                              </div>
                              <div>
                                <p className="font-semibold text-gray-900 text-sm">{request.user_name}</p>
                                <p className="text-xs text-gray-500">{request.leave_type_name}</p>
                              </div>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <p className="text-sm text-gray-700">
                                {format(parseISO(request.start_date), "dd/MM", { locale: fr })} - {format(parseISO(request.end_date), "dd/MM", { locale: fr })}
                              </p>
                              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-[10px] mt-1">
                                {request.days_count}j
                              </Badge>
                            </div>
                          </div>
                          {conflicts.length > 0 && (
                            <div className="mt-2 p-2 bg-orange-50 rounded-lg text-xs text-orange-700 flex items-center gap-1.5">
                              <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
                              {conflicts.length} collègue(s) absent(s) sur cette période
                            </div>
                          )}
                          {request.comment && (
                            <div className="mt-2 p-2 bg-gray-50 rounded text-xs text-gray-600 flex items-start gap-1.5">
                              <MessageSquare className="h-3 w-3 mt-0.5 flex-shrink-0" />
                              "{request.comment}"
                            </div>
                          )}
                          <div className="mt-3 flex justify-end gap-2">
                            <Dialog>
                              <DialogTrigger asChild>
                                <Button variant="outline" size="sm" onClick={() => setSelectedRequest(request)} data-testid={`review-${request.id}`}>
                                  <Eye className="h-3.5 w-3.5 mr-1.5" />Examiner
                                </Button>
                              </DialogTrigger>
                              <DialogContent className="max-w-[95vw] lg:max-w-2xl max-h-[90vh] overflow-y-auto">
                                <DialogHeader>
                                  <DialogTitle>Examiner la demande</DialogTitle>
                                </DialogHeader>
                                <div className="space-y-4 py-2">
                                  {/* Request summary */}
                                  <div className="p-4 bg-gray-50 rounded-lg">
                                    <div className="flex items-center gap-3">
                                      <div className="w-10 h-10 bg-amber-500 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">
                                        {request.user_name.split(' ').map(n => n[0]).join('').substring(0, 2)}
                                      </div>
                                      <div>
                                        <p className="font-semibold">{request.user_name}</p>
                                        <p className="text-sm text-gray-500">{request.leave_type_name} — {request.days_count} jour(s)</p>
                                        <p className="text-sm text-gray-500">Du {format(parseISO(request.start_date), "dd MMM yyyy", { locale: fr })} au {format(parseISO(request.end_date), "dd MMM yyyy", { locale: fr })}</p>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Visual conflict timeline */}
                                  <div className="border border-gray-200 rounded-lg p-3 lg:p-4">
                                    <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-1.5">
                                      <CalendarIcon className="h-4 w-4 text-gray-500" />
                                      Absences de l'équipe sur la période
                                    </h4>
                                    <div className="overflow-x-auto">
                                      <ConflictTimeline request={request} teamRequests={teamRequests} />
                                    </div>
                                  </div>

                                  {request.comment && (
                                    <div className="p-3 bg-gray-50 rounded-lg text-sm text-gray-600 flex items-start gap-2">
                                      <MessageSquare className="h-4 w-4 mt-0.5 flex-shrink-0 text-gray-400" />
                                      <span>"{request.comment}"</span>
                                    </div>
                                  )}
                                  <Textarea placeholder="Commentaire (optionnel)..." value={reviewComment} onChange={(e) => setReviewComment(e.target.value)} rows={2} data-testid="review-comment" />
                                </div>
                                <DialogFooter className="gap-2">
                                  <Button variant="outline" onClick={() => handleReview('rejected')} disabled={reviewLoading} className="text-red-600 hover:bg-red-50" data-testid="reject-btn">
                                    <XCircle className="h-4 w-4 mr-1.5" />Refuser
                                  </Button>
                                  <Button onClick={() => handleReview('approved')} disabled={reviewLoading} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="approve-btn">
                                    <CheckCircle2 className="h-4 w-4 mr-1.5" />Approuver
                                  </Button>
                                </DialogFooter>
                              </DialogContent>
                            </Dialog>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </Card>
            </TabsContent>

            {/* ==================== HISTORY TAB ==================== */}
            <TabsContent value="history">
              <Card className="border-0 shadow-sm">
                <div className="px-4 lg:px-6 py-3 border-b border-gray-100">
                  <h3 className="text-sm lg:text-base font-semibold">Historique des demandes</h3>
                </div>
                <ScrollArea className="h-[500px]">
                  {teamRequests.filter(r => r.status !== 'pending').length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                      <Clock className="h-10 w-10 mb-3" />
                      <p className="text-sm">Aucun historique</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-100">
                      {teamRequests.filter(r => r.status !== 'pending').map((request) => (
                        <div key={request.id} className="p-3 lg:p-4 hover:bg-gray-50 transition-colors" data-testid={`history-${request.id}`}>
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2.5 min-w-0">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0 ${request.status === 'approved' ? 'bg-green-500' : 'bg-red-500'}`}>
                                {request.user_name.split(' ').map(n => n[0]).join('').substring(0, 2)}
                              </div>
                              <div className="min-w-0">
                                <p className="font-medium text-gray-900 text-sm truncate">{request.user_name}</p>
                                <p className="text-xs text-gray-500">{request.leave_type_name} — {request.days_count}j</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3 flex-shrink-0">
                              <span className="text-xs text-gray-500 hidden sm:inline">
                                {format(parseISO(request.start_date), "dd/MM", { locale: fr })} - {format(parseISO(request.end_date), "dd/MM", { locale: fr })}
                              </span>
                              {getStatusBadge(request.status)}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
}
